/**
 * Self-serve provisioning state.
 *
 * These records are written by admin-web's self-serve flows (Vercel domains,
 * SendGrid sender authentication, Stripe Connect) and read back by the same
 * screens to render status and DNS instructions. They are *state*, not form
 * fields: nothing here is edited directly through the settings patch
 * allowlist, because every value is produced by a vendor API rather than typed
 * by a person.
 *
 * Secrets never live here. The Stripe connected-account id is duplicated onto
 * the organization document only so the UI can show which account is attached;
 * the value the payment adapters actually read is the param-store entry
 * `/{org}/STRIPE/CONNECT_ID`.
 */

export type DnsRecordType = 'A' | 'AAAA' | 'CNAME' | 'TXT' | 'MX';

/** One DNS entry the tenant has to create at their registrar. */
export interface DnsRecord {
  type: DnsRecordType;
  /** Host/name, as it should be entered at the registrar (fully qualified). */
  name: string;
  value: string;
  /** Which authentication check this record satisfies, when it is an email record. */
  purpose?: 'SPF' | 'DKIM' | 'DMARC' | 'MX';
  /** Populated once a check has run and this specific record is still wrong. */
  reason?: string;
  /** False once the vendor confirms this record resolves. */
  pending?: boolean;
  /**
   * Shown with the required records so it can be published in the same pass.
   * The vendor does not validate it, so it does not gate verification.
   */
  recommended?: boolean;
}

export type ProvisionStatus = 'pending' | 'verified' | 'failed';

/**
 * Where the patient portal is served.
 *
 * `subdomain` is a host under the CarePortals zone (`<slug>.portals.care`).
 * We own that DNS, so it needs no tenant action and verifies on creation.
 *
 * `custom` is the tenant's own hostname. It is registered against the
 * patient-next Vercel project, which hands back the records the tenant must
 * create; until those resolve the domain is `pending`.
 */
export interface PortalDomainConfig {
  domain: string;
  kind: 'subdomain' | 'custom';
  status: ProvisionStatus;
  /** Empty for `subdomain` — there is nothing for the tenant to do. */
  records?: DnsRecord[];
  addedAt?: string;
  verifiedAt?: string;
  lastCheckedAt?: string;
  /** Last failure from Vercel, shown verbatim to internal users. */
  error?: string;
  /**
   * Set when the hostname will serve but will not resolve back to *this*
   * organization, because patient-api derives the org key from the hostname.
   * Clearing it needs a code change, so it is recorded rather than hidden.
   */
  routingWarning?: string;
}

/**
 * Sender authentication for outbound email.
 *
 * One CarePortals SendGrid account authenticates every tenant domain, so this
 * holds the SendGrid `whitelabel/domains` id rather than any API key. The
 * tenant's `notification.fromEmail` is only deliverable once `status` is
 * `verified` for the domain part of that address.
 */
export interface EmailDomainConfig {
  domain: string;
  /** SendGrid authenticated-domain id, from `POST /v3/whitelabel/domains`. */
  sendgridDomainId?: number;
  status: ProvisionStatus;
  records?: DnsRecord[];
  addedAt?: string;
  verifiedAt?: string;
  lastCheckedAt?: string;
  error?: string;
}

/**
 * The tenant's Stripe account, attached by OAuth.
 *
 * Display state only — see the file header. `livemode` records which Stripe
 * mode the authorization was granted in, so a test-mode connection cannot be
 * mistaken for a live one on the settings screen.
 */
export interface StripeConnectConfig {
  accountId: string;
  livemode: boolean;
  accountName?: string;
  email?: string;
  country?: string;
  defaultCurrency?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  connectedAt: string;
  /** Email of the staff user who completed the OAuth handshake. */
  connectedBy?: string;
  lastCheckedAt?: string;
}
